import type { FmDashboardRegionRecipeDescriptor } from './FmDashboardOverview.types';

type UnknownRecord = Record<string, unknown>;

export type DashboardRecipeIdentity = Pick<
  FmDashboardRegionRecipeDescriptor,
  'dominantBeat' | 'primaryAction' | 'expertReveal' | 'conversionRole'
>;

interface DashboardRecipeInput {
  requiredServedFields: readonly [string, ...string[]];
  requiredProvenance: readonly [string, ...string[]];
  transition: string;
}

const CALM_STATE_BEHAVIOR = {
  ready: 'Show the current answer and top-record action.',
  loading: 'Keep the owner question visible while the answer is prepared.',
  empty: 'Explain the empty portfolio and keep the forward action available.',
  partial: 'Show the served answer and identify the missing input.',
  stale: 'Keep the last answer visible with update context.',
  locked: 'Keep the portfolio visible and explain the access action.',
  error: 'Keep the owner question visible and offer a retry.',
} as const;

const RANKED_STATE_BEHAVIOR = {
  ready: 'Show every served record in rank order.',
  loading: 'Show progress without placeholder records.',
  empty: 'Explain how to create the first ranked record.',
  partial: 'Show complete served records and identify the missing remainder.',
  stale: 'Keep the served order visible with refresh context.',
  locked: 'Explain access without hiding the route forward.',
  error: 'Keep the region question visible and offer a retry.',
} as const;

/** Fix the calm region's visual/state contract while callers name its data ownership. */
export function defineDashboardCalmAnswerRecipe(
  input: DashboardRecipeInput,
): FmDashboardRegionRecipeDescriptor {
  return {
    dominantBeat: 'literal-answer',
    requiredServedFields: input.requiredServedFields,
    requiredProvenance: input.requiredProvenance,
    primaryAction: 'top-record-primary-action',
    expertReveal: 'none',
    transition: input.transition,
    stateBehavior: CALM_STATE_BEHAVIOR,
    mobileOrder: [
      'state',
      'literal-answer',
      'top-record-primary-action',
      'portfolio-assistant',
      'utility-actions',
    ],
    conversionRole: 'orientation',
  };
}

/** Fix the ranked region's visual/state contract while callers name its data ownership. */
export function defineDashboardRankedCollectionRecipe(
  input: DashboardRecipeInput,
): FmDashboardRegionRecipeDescriptor {
  return {
    dominantBeat: 'ranked-entity-grid',
    requiredServedFields: input.requiredServedFields,
    requiredProvenance: input.requiredProvenance,
    primaryAction: 'one-record-primary-action',
    expertReveal: 'same-record-table',
    transition: input.transition,
    stateBehavior: RANKED_STATE_BEHAVIOR,
    mobileOrder: ['state', 'literal-answer', 'ranked-entity-grid', 'same-record-table'],
    conversionRole: 'triage',
  };
}

const DASHBOARD_STATES = ['ready', 'loading', 'empty', 'partial', 'stale', 'locked', 'error'] as const;
const CONVERSION_ROLES = new Set(['orientation', 'triage', 'evidence', 'action', 'handoff']);

function failRecipe(path: string, message: string): never {
  throw new Error(`FmDashboardOverview: ${path} ${message}`);
}

function recipeRecord(value: unknown, path: string): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    failRecipe(path, 'must be an object');
  }
  return value as UnknownRecord;
}

function recipeText(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') failRecipe(path, 'must be a non-empty string');
  return value;
}

function recipeTextArray(value: unknown, path: string): string[] {
  if (!Array.isArray(value) || value.length === 0) failRecipe(path, 'must contain at least 1 item');
  const values = value.map((entry, index) => recipeText(entry, `${path}[${index}]`));
  if (new Set(values).size !== values.length) failRecipe(path, 'must not contain duplicates');
  return values;
}

/** Runtime-seal a region recipe to the shared dashboard contract. */
export function assertDashboardRegionRecipe(
  value: unknown,
  path: string,
  expected: DashboardRecipeIdentity,
): void {
  const recipe = recipeRecord(value, path);
  const allowed = [
    'dominantBeat', 'requiredServedFields', 'requiredProvenance', 'primaryAction',
    'expertReveal', 'transition', 'stateBehavior', 'mobileOrder', 'conversionRole',
  ];
  const unexpected = Object.keys(recipe).filter((key) => !allowed.includes(key));
  if (unexpected.length > 0) failRecipe(path, `contains unknown key "${unexpected[0]}"`);
  const identityKeys = ['dominantBeat', 'primaryAction', 'expertReveal', 'conversionRole'] as const;
  identityKeys.forEach((key) => {
    const actual = recipeText(recipe[key], `${path}.${key}`);
    if (actual !== expected[key]) failRecipe(`${path}.${key}`, `must be ${expected[key]}`);
  });
  recipeTextArray(recipe.requiredServedFields, `${path}.requiredServedFields`);
  recipeTextArray(recipe.requiredProvenance, `${path}.requiredProvenance`);
  recipeText(recipe.transition, `${path}.transition`);
  const stateBehavior = recipeRecord(recipe.stateBehavior, `${path}.stateBehavior`);
  const stateKeys = Object.keys(stateBehavior);
  if (stateKeys.length !== DASHBOARD_STATES.length || stateKeys.some((key) => !DASHBOARD_STATES.includes(key as typeof DASHBOARD_STATES[number]))) {
    failRecipe(`${path}.stateBehavior`, 'must define exactly ready, loading, empty, partial, stale, locked, and error');
  }
  DASHBOARD_STATES.forEach((state) => recipeText(stateBehavior[state], `${path}.stateBehavior.${state}`));
  const mobileOrder = recipeTextArray(recipe.mobileOrder, `${path}.mobileOrder`);
  if (!mobileOrder.includes(String(recipe.dominantBeat))) {
    failRecipe(`${path}.mobileOrder`, 'must include dominantBeat');
  }
  if (!CONVERSION_ROLES.has(String(recipe.conversionRole))) {
    failRecipe(`${path}.conversionRole`, 'contains an unsupported value');
  }
}
